import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { useState } from 'react';
import { DateTime } from 'luxon';
import { Progress, ErrorPanel, Table, MarkdownContent } from '@backstage/core-components';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import { makeStyles } from '@material-ui/core/styles';
import { useApi, alertApiRef } from '@backstage/core-plugin-api';
import { EntityDialog } from './EntityDialog.esm.js';
import { catalogUnprocessedEntitiesApiRef } from '../api/index.esm.js';
import useAsync from 'react-use/esm/useAsync';
import DeleteIcon from '@material-ui/icons/Delete';
import { DeleteEntityConfirmationDialog } from './DeleteEntityConfirmationDialog.esm.js';

const useStyles = makeStyles((theme) => ({
  errorBox: {
    color: theme.palette.status.error,
    backgroundColor: theme.palette.errorBackground,
    padding: "1em",
    margin: "1em",
    border: `1px solid ${theme.palette.status.error}`
  },
  errorTitle: {
    width: "100%",
    fontWeight: "bold"
  },
  successMessage: {
    background: theme.palette.infoBackground,
    color: theme.palette.infoText,
    padding: theme.spacing(2)
  }
}));
const RenderErrorContext = ({
  error,
  rowData
}) => {
  if (error.message.includes("tags.")) {
    return /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx(Typography, { children: "Tags" }),
      /* @__PURE__ */ jsx("ul", { children: rowData.unprocessed_entity.metadata.tags?.map((t) => /* @__PURE__ */ jsx("li", { children: t })) })
    ] });
  }
  if (error.message.includes("metadata.name")) {
    return /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx(Typography, { children: "Name" }),
      /* @__PURE__ */ jsx(Typography, { variant: "caption", children: rowData.unprocessed_entity.metadata.name })
    ] });
  }
  return null;
};
const convertTimeToLocalTimezone = (dateTime) => {
  const isoDateTime = typeof dateTime === "string" ? dateTime : dateTime.toISOString();
  const strDateTime = DateTime.fromISO(isoDateTime, {
    zone: DateTime.local().zoneName
  });
  return strDateTime.toFormat("yyyy-MM-dd hh:mm:ss ZZZZ");
};
const FailedEntities = () => {
  const classes = useStyles();
  const unprocessedApi = useApi(catalogUnprocessedEntitiesApiRef);
  const {
    loading,
    error,
    value: data
  } = useAsync(async () => await unprocessedApi.failed());
  const [, setSelectedSearchTerm] = useState("");
  const unprocessedEntityApi = useApi(catalogUnprocessedEntitiesApiRef);
  const alertApi = useApi(alertApiRef);
  const [selectedEntityId, setSelectedEntityId] = useState(
    void 0
  );
  const [selectedEntityRef, setSelectedEntityRef] = useState(void 0);
  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
  if (loading) {
    return /* @__PURE__ */ jsx(Progress, {});
  }
  if (error) {
    return /* @__PURE__ */ jsx(ErrorPanel, { error });
  }
  const handleDelete = ({
    entityId,
    entityRef
  }) => {
    setSelectedEntityId(entityId);
    setSelectedEntityRef(entityRef);
    setConfirmationDialogOpen(true);
  };
  const cleanUpAfterRemoval = async () => {
    try {
      if (selectedEntityId) {
        await unprocessedEntityApi.delete(selectedEntityId);
        alertApi.post({
          message: `Entity ${selectedEntityRef} has been deleted`,
          severity: "success"
        });
      }
    } catch (e) {
      alertApi.post({
        message: `Ran into an issue when deleting ${selectedEntityRef}. Please try again later.`,
        severity: "error"
      });
    }
    setConfirmationDialogOpen(false);
  };
  const columns = [
    {
      title: /* @__PURE__ */ jsx(Typography, { children: "entityRef" }),
      sorting: true,
      field: "entity_ref",
      customFilterAndSearch: (query, row) => row.entity_ref.toLocaleUpperCase("en-US").includes(query.toLocaleUpperCase("en-US")),
      render: (rowData) => rowData.entity_ref
    },
    {
      title: /* @__PURE__ */ jsx(Typography, { children: "Location Path" }),
      sorting: true,
      field: "location_key",
      render: (rowData) => rowData.location_key
    },
    {
      title: /* @__PURE__ */ jsx(Typography, { children: "Kind" }),
      sorting: true,
      field: "kind",
      render: (rowData) => rowData.unprocessed_entity.kind
    },
    {
      title: /* @__PURE__ */ jsx(Typography, { children: "Owner" }),
      sorting: true,
      field: "unprocessed_entity.spec.owner",
      render: (rowData) => rowData.unprocessed_entity.spec?.owner || "unknown"
    },
    {
      title: /* @__PURE__ */ jsx(Typography, { children: "Last Discovery At" }),
      sorting: true,
      field: "last_discovery_at",
      render: (rowData) => convertTimeToLocalTimezone(
        rowData.last_discovery_at
      ) || "unknown"
    },
    {
      title: /* @__PURE__ */ jsx(Typography, { children: "Next Refresh At" }),
      sorting: true,
      field: "next_update_at",
      render: (rowData) => convertTimeToLocalTimezone(
        rowData.next_update_at
      ) || "unknown"
    },
    {
      title: /* @__PURE__ */ jsx(Typography, { children: "Raw Entity Definition" }),
      sorting: false,
      render: (rowData) => /* @__PURE__ */ jsx(EntityDialog, { entity: rowData })
    },
    {
      title: /* @__PURE__ */ jsx(Typography, { children: "Actions" }),
      render: (rowData) => {
        const { entity_id, entity_ref } = rowData;
        return /* @__PURE__ */ jsx(
          IconButton,
          {
            "aria-label": "delete",
            onClick: () => handleDelete({
              entityId: entity_id,
              entityRef: entity_ref
            }),
            children: /* @__PURE__ */ jsx(DeleteIcon, { fontSize: "small", "data-testid": "delete-icon" })
          }
        );
      }
    }
  ];
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(
      Table,
      {
        options: { pageSize: 20, search: true },
        columns,
        data: data?.entities ?? [],
        emptyContent: /* @__PURE__ */ jsx(Typography, { className: classes.successMessage, children: "No failed entities found" }),
        onSearchChange: (searchTerm) => setSelectedSearchTerm(searchTerm),
        detailPanel: ({ rowData }) => {
          const errors = rowData.errors;
          return /* @__PURE__ */ jsx(Fragment, { children: errors?.map((e, idx) => {
            return /* @__PURE__ */ jsxs(Box, { className: classes.errorBox, children: [
              /* @__PURE__ */ jsx(Typography, { className: classes.errorTitle, children: e.name }),
              /* @__PURE__ */ jsx(MarkdownContent, { content: e.message }),
              /* @__PURE__ */ jsx(
                RenderErrorContext,
                {
                  error: e,
                  rowData
                }
              )
            ] }, idx);
          }) });
        }
      }
    ),
    /* @__PURE__ */ jsx(
      DeleteEntityConfirmationDialog,
      {
        open: confirmationDialogOpen,
        onClose: () => setConfirmationDialogOpen(false),
        onConfirm: cleanUpAfterRemoval
      }
    )
  ] });
};

export { FailedEntities, convertTimeToLocalTimezone };
//# sourceMappingURL=FailedEntities.esm.js.map
